import React, { useEffect, useState } from "react";
import { Anchor, Flag, CalendarClock } from "lucide-react";
import { branchElectionLabel } from "@/lib/constants";

const targetOf = (dateStr) => (dateStr ? new Date(dateStr + "T08:00:00+03:00") : null);

const diff = (target) => {
  if (!target) return null;
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
};

const pad = (n) => String(n).padStart(2, "0");

const Tile = ({ value, label, testid, pulse }) => (
  <div
    className="flex flex-col items-center justify-center rounded-xl px-2 py-3 sm:px-4 sm:py-4 min-w-[64px] sm:min-w-[92px]"
    style={{
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.14)",
      backdropFilter: "blur(6px)",
    }}
    data-testid={testid}
  >
    <span
      className="font-display font-bold tabular-nums leading-none text-white text-3xl sm:text-5xl"
      style={pulse ? { animation: "ecTick 1s ease-in-out infinite" } : undefined}
    >
      {value}
    </span>
    <span className="mt-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.6)" }}>
      {label}
    </span>
  </div>
);

export const ElectionCountdown = ({ dateStr = "2026-10-11", title = "İMEAK DTO Seçimi" }) => {
  const target = targetOf(dateStr);
  const [t, setT] = useState(diff(target));

  useEffect(() => {
    setT(diff(target));
    const id = setInterval(() => setT(diff(target)), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  const unknown = !dateStr;
  const over = !unknown && t === null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl px-4 py-5 sm:px-7 sm:py-6"
      style={{
        background: "linear-gradient(120deg, #0a1e3f 0%, #103a63 55%, #0c5679 100%)",
        boxShadow: "0 10px 40px -12px rgba(4,20,40,0.6)",
      }}
      data-testid="election-countdown"
    >
      <style>{`@keyframes ecTick{0%,100%{opacity:1;transform:translateY(0)}50%{opacity:.55;transform:translateY(-1px)}}`}</style>
      <div className="pointer-events-none absolute -top-16 -right-10 h-52 w-52 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(56,189,248,0.35), transparent)" }} />
      <div className="pointer-events-none absolute -bottom-20 left-10 h-52 w-52 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(56,189,248,0.16), transparent)" }} />

      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(56,189,248,0.18)", border: "1px solid rgba(56,189,248,0.35)" }}>
            <Anchor className="h-5 w-5" style={{ color: "#7dd3fc" }} />
          </div>
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest" style={{ color: "#7dd3fc" }}>
              <Flag className="h-3.5 w-3.5" /> Seçime Kalan Süre
            </p>
            <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-tight mt-0.5" data-testid="election-countdown-title">
              {title}{unknown ? "" : ` · ${branchElectionLabel(dateStr)}`}
            </h2>
            {over && (
              <p className="text-xs sm:text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.65)" }}>
                Seçim günü geldi — sandık başına!
              </p>
            )}
          </div>
        </div>

        {unknown ? (
          <div className="flex items-center gap-2 rounded-xl px-5 py-4 text-center" style={{ background: "rgba(255,255,255,0.1)" }} data-testid="ec-unknown">
            <CalendarClock className="h-5 w-5" style={{ color: "#7dd3fc" }} />
            <span className="font-display text-base sm:text-lg font-bold text-white">Seçim tarihi henüz belirlenmedi</span>
          </div>
        ) : over ? (
          <div className="rounded-xl px-5 py-4 text-center" style={{ background: "rgba(255,255,255,0.1)" }}>
            <span className="font-display text-2xl font-bold text-white">Bugün Seçim Günü!</span>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            <Tile value={t.days} label="Gün" testid="ec-days" />
            <Tile value={pad(t.hours)} label="Saat" testid="ec-hours" />
            <Tile value={pad(t.minutes)} label="Dakika" testid="ec-minutes" />
            <Tile value={pad(t.seconds)} label="Saniye" testid="ec-seconds" pulse />
          </div>
        )}
      </div>
    </div>
  );
};
